import { FC, useEffect, useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { usePermission } from 'hooks';
import { Messages } from 'constants/rpcTypes';
import api from 'api/user';
import { RootState } from 'store';
import checkFilters from 'helpers/checkFilters';
import MenuContext from 'components/ui/menuContext';
import Icon from 'components/ui/icon';
import { updateLanguage } from 'actions/user';
import './changeLang.scss';
import { ComponentRegistry } from 'runtime/registry/components/ComponentRegistry';

export const ChangeLang: FC = () => {
  const dispatch = useDispatch();
  const { isEnabled } = usePermission();
  const interfaceLang = useSelector(
    (state: RootState) => state.localization.interfaceLang
  );
  const languagesAvailable =
    useSelector(
      (state: RootState) => state.filtersValues?.interfaceLanguage?.list
    ) || [];

  useEffect(() => {
    checkFilters(['interfaceLanguage']);
  }, []);

  const handleChangeLang = async ({ id: newLang }) => {
    if (newLang === interfaceLang) {
      return;
    }

    if (newLang === 'xx') {
      dispatch(
        updateLanguage({
          interfaceLang: newLang,
        })
      );
      return;
    }

    try {
      await api.updateUserLanguage(newLang);
    } catch (err) {
      console.error(err);
    }
  };

  const languages: {
    id: string;
    text: string;
    countryCode?: string;
  }[] = useMemo(() => {
    if (isEnabled(Messages.Localize_LexemesList) && languagesAvailable.length) {
      return [...languagesAvailable, { id: 'xx', text: 'Keys' }];
    }

    return languagesAvailable;
  }, [languagesAvailable]);

  const getList = useMemo(() => {
    return languages.map(({ id, text, countryCode }) => ({
      id,
      active: id === interfaceLang,
      content: (
        <LanguageItem
          key={id}
          text={text}
          countryCode={countryCode}
          isActive={id === interfaceLang}
        />
      ),
      onClick: handleChangeLang,
    }));
  }, [interfaceLang, languages, handleChangeLang]);

  if (!languages.length) {
    return null;
  }

  const list = languages.map(({ id, text, countryCode }) => ({
    id,
    active: id === interfaceLang,
    content: (
      <LanguageItem
        key={id}
        text={text}
        countryCode={countryCode}
        isActive={id === interfaceLang}
      />
    ),
    onClick: handleChangeLang,
  }));

  const props = ComponentRegistry.getProps('ChangeLang')

  return (
    <MenuContext
      name={classNames('lang')}
      customClass={classNames('change-lang')}
      panelCustomClass={props?.panelCustomClass}
      list={list}
      value={{
        buttonContent: (
          <div className='change-lang__item change-lang__item_btn'>
            <span className='change-lang__text'>
              {languages.find(({ id }) => id === interfaceLang)?.text}
            </span>
            <Icon
              name='im-Arrow-down'
              size={10}
              className='change-lang__arrow-icon'
            />
          </div>
        ),
      }}
    />
  );
};

const LanguageItem: FC<{
  text: string;
  isActive: boolean;
  countryCode?: string;
}> = ({ text, countryCode, isActive }) => {
  return (
    <div
      className={classNames('change-lang__item', {
        'change-lang_active': isActive,
      })}>
      <div className='change-lang__info'>
        {countryCode ? (
          <ReactCountryFlag
            countryCode={countryCode}
            svg
            style={{
              width: 16,
              height: 16,
              borderRadius: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Icon name='im-Info' size={16} className='change-lang__info-icon' />
        )}
        <span className='change-lang__text'>{text}</span>
      </div>
      <Icon
        name='im-Check-mark'
        size={10}
        className='change-lang__check-icon'
      />
    </div>
  );
};
